/**
 * Currency formatting utilities
 */

let activeCurrency = 'COP';
let activeLocale = 'es-CO';

// Currencies without a fractional unit in everyday use.
export const ZERO_DECIMAL_CURRENCIES = new Set(['COP', 'CLP', 'PYG', 'JPY', 'KRW', 'VND', 'ISK', 'UGX']);

/** Set by LocalizationContext so formatPrice() defaults to the selected country's currency and number format. */
export const setActiveCurrency = (currency, locale) => {
  if (currency) activeCurrency = currency;
  if (locale) activeLocale = locale;
};

/**
 * Format price with currency symbol and ISO code, e.g. "$150 USD", "$280.000 COP".
 * @param {number} price - The price to format
 * @param {string} currency - Currency code (default: the selected country's currency)
 * @returns {string} Formatted price string
 */
export const formatPrice = (price, currency) => {
  const code = (currency || activeCurrency || 'COP').toUpperCase();
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  const value = numPrice === null || numPrice === undefined || isNaN(numPrice) ? 0 : numPrice;
  const digits = ZERO_DECIMAL_CURRENCIES.has(code) || Number.isInteger(value) ? 0 : 2;
  // The selected country's own number format (es-AR "1.234,5", en-US "1,234.5"); other currencies (e.g. an old order) use en-US.
  const locale = code === 'COP' ? 'es-CO' : code === activeCurrency ? activeLocale : 'en-US';
  const options = { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol', minimumFractionDigits: digits, maximumFractionDigits: digits };
  let parts;
  try {
    parts = new Intl.NumberFormat(locale, options).formatToParts(value);
  } catch (e) {
    parts = new Intl.NumberFormat('en-US', options).formatToParts(value);
  }
  // Glue the symbol to the number ("R$ 1,50" -> "R$1,50") but keep digit-group spacing ("49 189").
  const text = parts
    .filter((part, i) => !(part.type === 'literal' && /^\s+$/.test(part.value) && (parts[i - 1]?.type === 'currency' || parts[i + 1]?.type === 'currency')))
    .map((part) => part.value)
    .join('');
  return `${text} ${code}`;
};

/**
 * Format price without currency symbol
 * @param {number} price - The price to format
 * @returns {string} Formatted price string without symbol
 */
export const formatPriceNumber = (price) => {
  if (price === null || price === undefined || isNaN(price)) {
    return '0';
  }

  const numPrice = typeof price === 'string' ? parseFloat(price) : price;

  return new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numPrice);
};

/**
 * Calculate discount percentage
 * @param {number} originalPrice - Original price
 * @param {number} salePrice - Sale price
 * @returns {number} Discount percentage
 */
export const calculateDiscount = (originalPrice, salePrice) => {
  if (!originalPrice || !salePrice || salePrice >= originalPrice) {
    return 0;
  }
  return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
};
